/**
 * Inventories Service
 *
 * API calls for Inventories.
 * @see US-12.2 - API CRUD
 * @see US-12.3 - Auto-save
 * @see US-12.5 - Validation
 */

import { apiClient } from '@/api/client';
import type {
  InventoryListItem,
  InventoryWithLines,
  CreateInventoryDto,
  UpdateInventoryDto,
  CancelInventoryDto,
  QueryInventoriesDto,
  SaveProductLinesDto,
  ApiSuccess,
  InventoriesListResponse,
  InventoryDetailResponse,
  InventoryCreateResponse,
  InventoryUpdateResponse,
  InventoryDeleteResponse,
  InventorySummaryResponse,
  InventoryPreviewResponse,
  ValidateInventoryResponse,
  CancelInventoryResponse,
  InventoriesKPIsResponse,
  SaveProductLinesApiResponse,
} from '@bstock/shared';

// Re-export types for convenience
export type {
  InventoryListItem,
  InventoryWithLines,
  CreateInventoryDto,
  UpdateInventoryDto,
  CancelInventoryDto,
  QueryInventoriesDto,
  SaveProductLinesDto,
  InventorySummaryResponse,
  InventoryPreviewResponse,
  InventoriesKPIsResponse,
};

export const inventoriesService = {
  /**
   * List inventories with pagination and filters
   */
  async list(params: QueryInventoriesDto): Promise<InventoriesListResponse> {
    const response = await apiClient.get<ApiSuccess<InventoriesListResponse>>(
      '/inventories',
      { params }
    );
    return response.data.data;
  },

  /**
   * Get inventories KPIs
   */
  async getKPIs(startDate?: string, endDate?: string): Promise<InventoriesKPIsResponse> {
    const response = await apiClient.get<ApiSuccess<InventoriesKPIsResponse>>(
      '/inventories/kpis',
      { params: { startDate, endDate } }
    );
    return response.data.data;
  },

  /**
   * Get single inventory by ID
   */
  async getById(id: string): Promise<InventoryDetailResponse> {
    const response = await apiClient.get<ApiSuccess<InventoryDetailResponse>>(
      `/inventories/${id}`
    );
    return response.data.data;
  },

  /**
   * Get inventory summary
   */
  async getSummary(id: string): Promise<InventorySummaryResponse> {
    const response = await apiClient.get<ApiSuccess<InventorySummaryResponse>>(
      `/inventories/${id}/summary`
    );
    return response.data.data;
  },

  /**
   * Get inventory preview before validation
   */
  async getPreview(id: string): Promise<InventoryPreviewResponse> {
    const response = await apiClient.get<ApiSuccess<InventoryPreviewResponse>>(
      `/inventories/${id}/preview`
    );
    return response.data.data;
  },

  /**
   * Create a new inventory
   */
  async create(data: CreateInventoryDto): Promise<InventoryCreateResponse> {
    const response = await apiClient.post<ApiSuccess<InventoryCreateResponse>>(
      '/inventories',
      data
    );
    return response.data.data;
  },

  /**
   * Update an inventory (metadata only)
   */
  async update(id: string, data: UpdateInventoryDto): Promise<InventoryUpdateResponse> {
    const response = await apiClient.patch<ApiSuccess<InventoryUpdateResponse>>(
      `/inventories/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete an inventory (DRAFT only)
   */
  async delete(id: string): Promise<InventoryDeleteResponse> {
    const response = await apiClient.delete<ApiSuccess<InventoryDeleteResponse>>(
      `/inventories/${id}`
    );
    return response.data.data;
  },

  /**
   * Save all lines for a product (bulk + packaging)
   * @see US-21b.05
   */
  async saveProductLines(
    inventoryId: string,
    rawProductId: string,
    data: SaveProductLinesDto
  ): Promise<SaveProductLinesApiResponse> {
    const response = await apiClient.put<ApiSuccess<SaveProductLinesApiResponse>>(
      `/inventories/${inventoryId}/products/${rawProductId}/lines`,
      data
    );
    return response.data.data;
  },

  /**
   * Validate an inventory
   */
  async validate(id: string): Promise<ValidateInventoryResponse> {
    const response = await apiClient.post<ApiSuccess<ValidateInventoryResponse>>(
      `/inventories/${id}/validate`
    );
    return response.data.data;
  },

  /**
   * Cancel an inventory
   */
  async cancel(id: string, data?: CancelInventoryDto): Promise<CancelInventoryResponse> {
    const response = await apiClient.post<ApiSuccess<CancelInventoryResponse>>(
      `/inventories/${id}/cancel`,
      data ?? {}
    );
    return response.data.data;
  },

  /**
   * Download inventory counting sheet (PDF or Excel)
   * @see US-39.1
   */
  async downloadSheet(inventoryId: string, format: 'pdf' | 'excel'): Promise<void> {
    const ext = format === 'pdf' ? 'pdf' : 'xlsx';
    const response = await apiClient.get(
      `/inventories/${inventoryId}/sheet/${format}`,
      { responseType: 'blob' },
    );
    // Extract filename from Content-Disposition header or fallback
    const disposition = response.headers['content-disposition'] ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `feuille-inventaire.${ext}`;

    const url = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
};
